import { useEffect } from "react"

import NumberFlow from "@number-flow/react"
import { Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import Private from "../../../../Components/Private"
import Bone from "../../../../Components/Skeleton/Bone"
import { RoutesPaths } from "../../../../constants/user-routes"
import { useAnimatedNumber } from "../../../../hooks/useAnimatedNumber"
import Icon from "../../../../Icon"
import { STATUS_META } from "../../../ReceiptPage/ConfigureBusinessPage/constants/constants"
import { changeBusinessFilters } from "../../../ReceiptPage/ConfigureBusinessPage/redux/business.slice"
import { DEFLECT_SECTION } from "../../redux/deflectAnalytics.constants"
import { selectDeflectFirstLoad, selectDeflectSection } from "../../redux/deflectAnalytics.slice"

import styles from "./queueCards.module.less"

const { Text } = Typography

const statusesOf = (group) =>
  Object.keys(STATUS_META).filter((status) => STATUS_META[status].group === group)

// `status` pre-sets Configure → Business's status filter before the page opens.
const ITEMS = [
  {
    id: "needs_help",
    label: "Descriptors need help",
    icon: "alert_triangle_error",
    tone: "orange",
    tooltip:
      "Descriptors sitting in the Needs Help status group. Support is waiting on something from you.",
    to: RoutesPaths.receipts.configure_business,
    status: statusesOf("needs_help")
  },
  {
    id: "pending",
    label: "Enrollment pending",
    icon: "clock_filled",
    tone: "purple",
    tooltip:
      "Descriptor-program records in the Pending status group, waiting on Ethoca or on support.",
    to: RoutesPaths.receipts.configure_business,
    status: statusesOf("pending")
  },
  {
    id: "profile_updates",
    label: "Profile updates in review",
    icon: "edit",
    tone: "blue",
    tooltip:
      "Receipt-content edits waiting on support. One record per Active program — outcomes can differ per program.",
    to: RoutesPaths.receipts.configure_profile
  }
]

const QueueItem = ({ label, value, icon, tone, tooltip, loading, onClick }) => {
  const { t } = useTranslation()
  const animated = useAnimatedNumber(value)
  return (
    <div
      className={styles.item}
      role="button"
      tabIndex={0}
      aria-label={t(label)}
      // A tap on the info icon opens its tooltip; it must not also open the queue the row leads to.
      onClick={(e) => {
        if (!e.target.closest(".info-tooltip-icon-wrapper")) onClick()
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <Flex align="center" gap={8}>
        <Icon type={icon} className={cls(styles.icon, styles[tone])} />
        <Text type="secondary" strong style={{ fontSize: 14 }}>
          {t(label)}
        </Text>
        <InfoTooltip title={t(tooltip)} />
      </Flex>
      <Text style={{ fontSize: 24, lineHeight: 1.4 }}>
        {loading ? (
          <Bone width={40} height={24} />
        ) : value == null ? (
          "-"
        ) : (
          <Private>
            <NumberFlow value={animated} format={{ maximumFractionDigits: 0 }} />
          </Private>
        )}
      </Text>
    </div>
  )
}

// The live queues: descriptors and profile edits waiting on someone, within the program scope
// but not the calendar — a date window would silently hide work.
const QueueCards = ({ getData }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const counts = useSelector(selectDeflectSection(DEFLECT_SECTION.QUEUE)) ?? {}
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.QUEUE))

  useEffect(() => {
    getData(DEFLECT_SECTION.QUEUE)
  }, [getData])

  const openQueue = ({ to, status }) => {
    if (status) dispatch(changeBusinessFilters({ status }))
    navigate(to)
  }

  return (
    <div className={styles.bar}>
      {ITEMS.map((item) => (
        <QueueItem
          key={item.id}
          {...item}
          value={counts[item.id]}
          loading={firstLoad}
          onClick={() => openQueue(item)}
        />
      ))}
    </div>
  )
}

export default QueueCards
